import { useEffect, useRef, useState } from 'react';

/**
 * Lazy-mount helper for media grids. Returns `[ref, inView]`: attach `ref` to a
 * container and `inView` flips to `true` once it approaches the viewport
 * (`rootMargin` ahead), then STAYS true (load-once — the observer disconnects).
 *
 * Used to defer mounting heavy `<video>` tiles so a long grid doesn't fetch
 * every clip at once (web.dev "Lazy loading video").
 */
export function useInViewport<T extends Element = HTMLDivElement>(
  rootMargin = '300px'
) {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    // SSR / very old browsers: no IntersectionObserver → load eagerly.
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView, rootMargin]);

  return [ref, inView] as const;
}
